import React, { useRef, useState, useEffect, useMemo } from "react";
import "./App.css";
import "./auth/AuthScreen.css";
import axios from "axios";
import QRCode from "qrcode.react";
import imageCompression from "browser-image-compression";
import { useAuth } from "./auth/AuthContext";
import AuthScreen from "./auth/AuthScreen";
import Navbar from "./components/Navbar";
import StudioStepTabs, { STUDIO_STEPS } from "./components/StudioStepTabs";
import DataQuotaBar from "./components/DataQuotaBar";
import { AtlasSetupPrompt } from "./components/AtlasPublish";
import Profile from "./Profile";
import Dashboard from "./Dashboard";
import {
  ARTIFACT_MAX_BYTES,
  PROJECT_MAX_BYTES,
  formatBytes,
  assertFileWithinLimit,
} from "./lib/limits";
import { artifactFilename, webArUrl } from "./lib/projectPaths";

const API = {
  createProject: "/.netlify/functions/createProject",
  signArtifactUpload: "/.netlify/functions/signArtifactUpload",
  finalizeProject: "/.netlify/functions/finalizeProject",
  getProject: "/.netlify/functions/getProject",
  deleteProject: "/.netlify/functions/deleteProject",
};

const VIEW_ORDER = { studio: 0, dashboard: 1, settings: 2, login: 3 };
const PROTECTED_VIEWS = new Set(["studio", "dashboard", "settings"]);

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

function isAndroid() {
  return typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent);
}

function sceneViewerUrl(glbUrl) {
  return `https://arvr.google.com/scene-viewer/1.0?file=${encodeURIComponent(glbUrl)}&mode=ar_preferred`;
}

function resolveOpenInAr(project) {
  const pref = project.ar_preference || "auto";
  const isWorld = project.tracking_type === "world";

  // Image tracking is WebAR-only (xr1). Quick Look / Scene Viewer need world tracking.
  if (!isWorld || pref === "webgl") {
    return { kind: "webgl", href: webArUrl(project) };
  }

  // World + auto | native: prefer platform native, then GLB, then WebAR zip host
  if (isIOS() && project.usdz_url) {
    return { kind: "quicklook", href: project.usdz_url };
  }
  if (project.glb_url) {
    if (isAndroid()) {
      return { kind: "sceneviewer", href: sceneViewerUrl(project.glb_url) };
    }
    return { kind: "modelviewer", href: project.glb_url, glbUrl: project.glb_url };
  }

  return { kind: "webgl", href: webArUrl(project) };
}

function nextStudioStep(current) {
  const id = current === "tracker" ? "basics" : current;
  const idx = STUDIO_STEPS.findIndex((s) => s.id === id);
  if (idx < 0 || idx >= STUDIO_STEPS.length - 1) return null;
  return STUDIO_STEPS[idx + 1].id;
}

function Studio({ onBackToDashboard }) {
  const zipInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const glbInputRef = useRef(null);
  const usdzInputRef = useRef(null);

  const [projectTitle, setProjectTitle] = useState("");
  const [trackingType, setTrackingType] = useState("image");
  const [arPreference, setArPreference] = useState("auto");
  const [project, setProject] = useState(null);
  const [step, setStep] = useState("basics");
  const [saved, setSaved] = useState(false);
  const [assets, setAssets] = useState({
    tracker: null,
    zip: null,
    glb: null,
    usdz: null,
  });
  const [preparing, setPreparing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState("");
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const { session } = useAuth();

  const trackerPreviewUrl = useMemo(() => {
    if (!assets.tracker) return null;
    return URL.createObjectURL(assets.tracker);
  }, [assets.tracker]);

  const glbPreviewUrl = useMemo(() => {
    if (!assets.glb) return null;
    return URL.createObjectURL(assets.glb);
  }, [assets.glb]);

  useEffect(() => {
    return () => {
      if (trackerPreviewUrl) URL.revokeObjectURL(trackerPreviewUrl);
    };
  }, [trackerPreviewUrl]);

  useEffect(() => {
    return () => {
      if (glbPreviewUrl) URL.revokeObjectURL(glbPreviewUrl);
    };
  }, [glbPreviewUrl]);

  const bytesByArtifact = Object.fromEntries(
    Object.entries(assets)
      .filter(([, file]) => file)
      .map(([key, file]) => [key, file.size])
  );
  const projectBytesUsed = Object.values(bytesByArtifact).reduce(
    (sum, n) => sum + (Number(n) || 0),
    0
  );
  const showQuota = !saved;
  const busy = preparing || submitting;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const authHeaders = () => ({
    Authorization: `Bearer ${session.access_token}`,
  });

  const apiErrorMessage = (err) => {
    const data = err?.response?.data;
    console.error("API error payload:", data || err?.message);
    if (data?.error === "PROJECT_QUOTA_EXCEEDED" || data?.message) {
      if (data.error === "PROJECT_QUOTA_EXCEEDED") {
        return data.message || "You have reached your project limit.";
      }
      if (typeof data === "object" && data.message) return data.message;
      if (typeof data === "object" && data.error) {
        const extra = [data.code, data.hint, data.details]
          .filter(Boolean)
          .join(" — ");
        return extra ? `${data.error} (${extra})` : data.error;
      }
    }
    if (typeof data === "string") return data;
    return err?.message || "Something went wrong. Please try again.";
  };

  const validateLocalFile = (artifact, file, currentAssets = assets) => {
    const check = assertFileWithinLimit(artifact, file.size);
    if (!check.ok) {
      const err = new Error(check.error);
      err.response = { data: { error: check.error, code: check.code } };
      throw err;
    }

    const usedWithoutThis = Object.entries(currentAssets).reduce(
      (sum, [key, f]) => {
        if (key === artifact || !f) return sum;
        return sum + f.size;
      },
      0
    );
    const nextTotal = usedWithoutThis + file.size;
    if (nextTotal > PROJECT_MAX_BYTES) {
      const msg = `Project data quota exceeded (${formatBytes(nextTotal)} / ${formatBytes(PROJECT_MAX_BYTES)}).`;
      const err = new Error(msg);
      err.response = { data: { error: msg, code: "PROJECT_DATA_QUOTA" } };
      throw err;
    }
  };

  const createProject = async (title, tracking_type, ar_preference) => {
    const response = await axios.post(
      API.createProject,
      { title, tracking_type, ar_preference },
      { headers: authHeaders() }
    );
    return response.data;
  };

  const deleteProject = async (projectId) => {
    try {
      await axios.post(
        API.deleteProject,
        { projectId },
        { headers: authHeaders() }
      );
    } catch (err) {
      console.error("Failed to delete project after failed submit:", err);
    }
  };

  const signAndUpload = async (
    projectId,
    artifact,
    file,
    usedSoFar,
    onProgress
  ) => {
    const check = assertFileWithinLimit(artifact, file.size);
    if (!check.ok) {
      const err = new Error(check.error);
      err.response = { data: { error: check.error, code: check.code } };
      throw err;
    }

    if (usedSoFar + file.size > PROJECT_MAX_BYTES) {
      const msg = `Project data quota exceeded (${formatBytes(usedSoFar + file.size)} / ${formatBytes(PROJECT_MAX_BYTES)}).`;
      const err = new Error(msg);
      err.response = { data: { error: msg, code: "PROJECT_DATA_QUOTA" } };
      throw err;
    }

    const signRes = await axios.post(
      API.signArtifactUpload,
      {
        projectId,
        artifact,
        contentType: file.type || "application/octet-stream",
        fileSize: file.size,
        projectBytesUsed: usedSoFar,
      },
      { headers: authHeaders() }
    );

    const { signedUrl } = signRes.data;
    await axios.put(signedUrl, file, {
      headers: { "Content-Type": file.type || "application/octet-stream" },
      onUploadProgress: (evt) => {
        if (!evt.total) return;
        const pct = Math.round((evt.loaded * 100) / evt.total);
        if (onProgress) onProgress(pct);
      },
    });

    return signRes.data;
  };

  const finalize = async (projectId, preference, mapOptions = {}) => {
    const payload = {
      projectId,
      ar_preference: preference,
    };
    if (mapOptions.on_map !== undefined) {
      payload.on_map = Boolean(mapOptions.on_map);
    }
    // Only forward real coordinates — never invent placement.
    if (mapOptions.latitude != null) payload.latitude = mapOptions.latitude;
    if (mapOptions.longitude != null) payload.longitude = mapOptions.longitude;
    const response = await axios.post(API.finalizeProject, payload, {
      headers: authHeaders(),
    });
    return response.data;
  };

  const refreshProject = async (projectId) => {
    const response = await axios.get(API.getProject, {
      params: { id: projectId },
    });
    return response.data;
  };

  const setTracking = (type) => {
    setError("");
    setTrackingType(type);
    if (type === "world") {
      setAssets((prev) => ({ ...prev, tracker: null }));
    } else {
      // Native AR viewers are world-only
      setArPreference((prev) => (prev === "native" ? "auto" : prev));
      setAssets((prev) => ({ ...prev, glb: null, usdz: null }));
    }
  };

  const handleStepSelect = (target) => {
    if (submitting || preparing || saved) return;
    setError("");
    setStep(target === "tracker" ? "basics" : target);
  };

  const goNext = () => {
    if (busy) return;
    const next = nextStudioStep(step);
    if (!next) return;
    setError("");
    setStep(next);
  };

  const handleTrackerSelect = async (e) => {
    let file = e.target.files?.[0];
    e.target.value = "";
    if (!file || trackingType !== "image") return;
    setError("");

    try {
      validateLocalFile("tracker", file);
    } catch (err) {
      console.error(err);
      setError(apiErrorMessage(err));
      return;
    }

    setPreparing(true);

    try {
      if (file.type !== "image/jpeg") {
        const compressed = await imageCompression(file, { useWebWorker: true });
        file = new File([compressed], "tracker.jpeg", { type: "image/jpeg" });
      } else {
        file = new File([file], "tracker.jpeg", { type: "image/jpeg" });
      }
      validateLocalFile("tracker", file);
      setAssets((prev) => ({ ...prev, tracker: file }));
    } catch (err) {
      console.error(err);
      setError(apiErrorMessage(err));
    } finally {
      setPreparing(false);
    }
  };

  const handleZipSelect = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError("");

    try {
      const zipFile =
        file.type && file.type !== ""
          ? file
          : new File([file], "model.zip", { type: "application/zip" });
      validateLocalFile("zip", zipFile);
      setAssets((prev) => ({ ...prev, zip: zipFile }));
    } catch (err) {
      console.error(err);
      setError(apiErrorMessage(err));
    }
  };

  const handleNativeSelect = (e, artifact) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || trackingType !== "world") return;
    setError("");

    try {
      const named =
        artifact === "glb"
          ? new File([file], "model.glb", {
              type: file.type || "model/gltf-binary",
            })
          : new File([file], "model.usdz", {
              type: file.type || "model/vnd.usdz+zip",
            });
      validateLocalFile(artifact, named);
      setAssets((prev) => ({ ...prev, [artifact]: named }));
    } catch (err) {
      console.error(err);
      setError(apiErrorMessage(err));
    }
  };

  const submitProject = async () => {
    if (!assets.zip) {
      setError("A zip model is required before saving.");
      return;
    }
    if (trackingType === "image" && !assets.tracker) {
      setError("A tracker image is required for image tracking.");
      return;
    }

    setError("");
    setSubmitting(true);
    setUploadProgress(0);
    setSubmitStatus("Creating project…");

    let createdId = null;
    try {
      const created = await createProject(
        projectTitle.trim(),
        trackingType,
        trackingType === "image" && arPreference === "native"
          ? "auto"
          : arPreference
      );
      createdId = created.id;
      setProject(created);

      const uploadOrder = [];
      if (assets.tracker) uploadOrder.push(["tracker", assets.tracker]);
      if (assets.zip) uploadOrder.push(["zip", assets.zip]);
      if (assets.glb) uploadOrder.push(["glb", assets.glb]);
      if (assets.usdz) uploadOrder.push(["usdz", assets.usdz]);

      let usedSoFar = 0;
      for (const [artifact, file] of uploadOrder) {
        const label =
          artifact === "tracker"
            ? "tracker"
            : artifact === "zip"
              ? "zip"
              : artifact.toUpperCase();
        setSubmitStatus(`Uploading ${label}…`);
        setUploadProgress(0);
        const named = new File(
          [file],
          artifactFilename(created.slug, artifact),
          { type: file.type || "application/octet-stream" }
        );
        await signAndUpload(
          createdId,
          artifact,
          named,
          usedSoFar,
          setUploadProgress
        );
        usedSoFar += named.size;
      }

      setSubmitStatus("Finalizing…");
      setUploadProgress(0);
      const preference =
        trackingType === "image" && arPreference === "native"
          ? "auto"
          : arPreference;
      await finalize(createdId, preference, { on_map: false });
      const full = await refreshProject(createdId);
      setProject(full);
      setSaved(true);
      setStep("publish");
    } catch (err) {
      console.error(err);
      setError(apiErrorMessage(err));
      if (createdId) {
        await deleteProject(createdId);
        setProject(null);
      }
    } finally {
      setSubmitting(false);
      setSubmitStatus("");
      setUploadProgress(0);
    }
  };

  const startAnother = () => {
    setProject(null);
    setProjectTitle("");
    setTrackingType("image");
    setArPreference("auto");
    setAssets({ tracker: null, zip: null, glb: null, usdz: null });
    setError("");
    setSaved(false);
    setStep("basics");
  };

  const qrSize = windowWidth <= 1200 ? 60 : 100;
  const openAr = project && saved ? resolveOpenInAr(project) : null;
  const shareHandle = project?.username ? `@${project.username}` : null;
  const onPublish = step === "publish";
  const footerDisabled = busy || (onPublish && !saved);

  return (
    <div className="studio-layout studio-layout--solo">
      <div className={`studio-panel ${saved ? "studio-panel--done" : ""}`}>
        <div className="studio-panel__face">
        <StudioStepTabs
          step={step}
          onSelect={handleStepSelect}
        />
        <div className="form-container">
        {error && <div className="studio-error">{error}</div>}
        {showQuota && <DataQuotaBar usedBytes={projectBytesUsed} />}

        <div className="studio-form-body">
        <div
          key={step === "tracker" ? "basics" : step}
          className="studio-step-pane"
        >
        {(step === "basics" || step === "tracker") && (
          <div className="setup-step">
            <label>
              <div className="title-container">Project title</div>
              <input
                type="text"
                value={projectTitle}
                onChange={(e) => setProjectTitle(e.target.value)}
                placeholder="My AR project"
                disabled={saved}
              />
            </label>

            <div className="title-container">Tracking type</div>
            <div className="seg-control" role="group" aria-label="Tracking type">
              <button
                type="button"
                className={`seg-control__btn ${
                  trackingType === "image" ? "seg-control__btn--active" : ""
                }`}
                onClick={() => setTracking("image")}
                disabled={saved}
              >
                Image
              </button>
              <button
                type="button"
                className={`seg-control__btn ${
                  trackingType === "world" ? "seg-control__btn--active" : ""
                }`}
                onClick={() => setTracking("world")}
                disabled={saved}
              >
                World
              </button>
            </div>

            {trackingType === "world" ? (
              <AtlasSetupPrompt onAtlas={Boolean(project?.on_map)} />
            ) : (
              <div
                className={`drop-zone drop-zone--compact ${
                  busy ? "drop-zone--busy" : ""
                } ${trackerPreviewUrl ? "drop-zone--has-preview" : ""}`}
                onClick={() => {
                  if (busy || saved) return;
                  imageInputRef.current.click();
                }}
              >
                {preparing ? (
                  "Preparing tracker…"
                ) : trackerPreviewUrl ? (
                  <>
                    <img
                      src={trackerPreviewUrl}
                      alt="Tracker"
                      className="tracker-preview"
                    />
                    <span className="size-hint">
                      Replace · {formatBytes(assets.tracker.size)}
                    </span>
                  </>
                ) : (
                  <>
                    Select your tracker image
                    <br />
                    <span className="size-hint">
                      Max {formatBytes(ARTIFACT_MAX_BYTES.tracker)}
                    </span>
                  </>
                )}
                <input
                  type="file"
                  accept="image/*"
                  ref={imageInputRef}
                  onChange={handleTrackerSelect}
                  disabled={busy || saved}
                  style={{ display: "none" }}
                />
              </div>
            )}
          </div>
        )}

        {step === "files" && (
          <div className="files-step">
            <div className="native-asset-row">
              <button
                type="button"
                className="button button-secondary"
                disabled={busy || saved}
                onClick={() => zipInputRef.current.click()}
              >
                {assets.zip
                  ? `ZIP ✓ (${formatBytes(assets.zip.size)})`
                  : "Add ZIP"}
              </button>
              <button
                type="button"
                className="button button-secondary"
                disabled={busy || saved || trackingType !== "world"}
                onClick={() => glbInputRef.current.click()}
                title={
                  trackingType !== "world"
                    ? "GLB is only used for world tracking (Scene Viewer)"
                    : undefined
                }
              >
                {trackingType !== "world"
                  ? "GLB (world only)"
                  : assets.glb
                    ? `GLB ✓ (${formatBytes(assets.glb.size)})`
                    : "Add GLB"}
              </button>
              <button
                type="button"
                className="button button-secondary"
                disabled={busy || saved || trackingType !== "world"}
                onClick={() => usdzInputRef.current.click()}
                title={
                  trackingType !== "world"
                    ? "USDZ is only used for world tracking (Quick Look)"
                    : undefined
                }
              >
                {trackingType !== "world"
                  ? "USDZ (world only)"
                  : assets.usdz
                    ? `USDZ ✓ (${formatBytes(assets.usdz.size)})`
                    : "Add USDZ"}
              </button>
            </div>
            <p className="size-hint" style={{ textAlign: "center", margin: 0 }}>
              ZIP max {formatBytes(ARTIFACT_MAX_BYTES.zip)}
              {trackingType !== "world"
                ? " · Quick Look / Scene Viewer require world tracking"
                : ""}
            </p>
            {glbPreviewUrl && (
              <div className="model-viewer-wrap">
                <model-viewer
                  src={glbPreviewUrl}
                  ar
                  ar-modes="webxr scene-viewer quick-look"
                  camera-controls
                  style={{ width: "100%", height: "220px" }}
                />
              </div>
            )}
            <input
              type="file"
              accept=".zip,application/zip"
              ref={zipInputRef}
              onChange={handleZipSelect}
              disabled={busy || saved}
              style={{ display: "none" }}
            />
            <input
              type="file"
              accept=".glb,model/gltf-binary"
              ref={glbInputRef}
              onChange={(e) => handleNativeSelect(e, "glb")}
              disabled={trackingType !== "world" || saved}
              style={{ display: "none" }}
            />
            <input
              type="file"
              accept=".usdz,model/vnd.usdz+zip"
              ref={usdzInputRef}
              onChange={(e) => handleNativeSelect(e, "usdz")}
              disabled={trackingType !== "world" || saved}
              style={{ display: "none" }}
            />
          </div>
        )}

        {step === "preference" && (
          <div className="ar-preference-selection">
            <div className="title-container" style={{ textAlign: "center" }}>
              AR preference
            </div>
            <button
              type="button"
              className={
                arPreference === "auto"
                  ? "button button-primary"
                  : "button button-secondary"
              }
              disabled={busy || saved}
              onClick={() => setArPreference("auto")}
            >
              Auto
            </button>
            <button
              type="button"
              className={
                arPreference === "native"
                  ? "button button-primary"
                  : "button button-secondary"
              }
              disabled={busy || saved || trackingType !== "world"}
              onClick={() => setArPreference("native")}
              title={
                trackingType !== "world"
                  ? "Native AR is only available for world tracking"
                  : undefined
              }
            >
              Native
            </button>
            <button
              type="button"
              className={
                arPreference === "webgl"
                  ? "button button-primary"
                  : "button button-secondary"
              }
              disabled={busy || saved}
              onClick={() => setArPreference("webgl")}
            >
              WebGL
            </button>
            <p className="deploy-blurb">
              <strong>Native</strong> — device AR (Quick Look / Scene Viewer) for
              world tracking. <strong>WebGL</strong> (ArgonWebgl) — browser-based
              AR via Argon’s WebGL viewers. <strong>Auto</strong> — prefer Native
              when available, otherwise WebGL.
            </p>
            {trackingType !== "world" && (
              <p className="size-hint" style={{ textAlign: "center", margin: 0 }}>
                Image tracking uses WebGL only (no Quick Look / Scene Viewer)
              </p>
            )}
          </div>
        )}

        {step === "publish" && (
          <div className="publish-step">
            {submitting ? (
              <div className="upload-message">
                {submitStatus}
                {uploadProgress > 0 ? ` ${uploadProgress}%` : ""}
              </div>
            ) : saved && project && openAr ? (
              <div className="upload-message share-panel">
                {shareHandle && (
                  <div className="share-username">{shareHandle}</div>
                )}
                <div className="share-title">{project.title}</div>
                {trackingType === "image" && trackerPreviewUrl && (
                  <img
                    src={trackerPreviewUrl}
                    alt="Tracker"
                    className="tracker-preview tracker-preview--publish"
                  />
                )}
                View your AR experience on a mobile device.
                <br />
                <br />
                <a
                  href={webArUrl(project)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div className="qr-code">
                    <QRCode
                      value={webArUrl(project)}
                      size={qrSize}
                      bgColor="transparent"
                      fgColor="#0a0a0a"
                    />
                  </div>
                  {webArUrl(project)}
                </a>

                {openAr.kind === "quicklook" && (
                  <a
                    rel="ar"
                    href={openAr.href}
                    className="button button-primary open-ar-btn"
                  >
                    <img
                      src={
                        project.thumbnail_url ||
                        project.tracker_url ||
                        "/favicon.ico"
                      }
                      alt=""
                      width="1"
                      height="1"
                      style={{
                        position: "absolute",
                        opacity: 0,
                        pointerEvents: "none",
                      }}
                    />
                    Open in AR (Quick Look)
                  </a>
                )}
                {openAr.kind === "sceneviewer" && (
                  <a
                    href={openAr.href}
                    className="button button-primary open-ar-btn"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open in AR (Scene Viewer)
                  </a>
                )}

                <button
                  type="button"
                  className="button button-secondary"
                  onClick={startAnother}
                >
                  Create another
                </button>
              </div>
            ) : (
              <>
                {trackingType === "image" && trackerPreviewUrl && (
                  <img
                    src={trackerPreviewUrl}
                    alt="Tracker"
                    className="tracker-preview tracker-preview--publish"
                  />
                )}
                <div className="qr-code qr-code--placeholder" aria-hidden="true">
                  <span className="qr-placeholder-logo">Ar+</span>
                </div>
                <p className="publish-hint">
                  A QR code will generate here when you save.
                </p>
                <button
                  type="button"
                  className="button button-primary"
                  disabled={busy}
                  onClick={submitProject}
                >
                  Save
                </button>
              </>
            )}
          </div>
        )}

        </div>
        </div>

        <button
          type="button"
          className="button button-secondary studio-back-btn"
          disabled={footerDisabled}
          onClick={() => {
            if (onPublish) {
              if (saved) onBackToDashboard();
              return;
            }
            goNext();
          }}
        >
          {onPublish ? "Go to Dashboard" : "Next"}
        </button>
        </div>
        </div>
      </div>
    </div>
  );
}

function App() {
  const { session, loading, signOut, user, passwordRecovery } = useAuth();
  const [view, setView] = useState("studio");
  const [navDir, setNavDir] = useState(0);
  const [pendingView, setPendingView] = useState(null);
  const [dashboardKey, setDashboardKey] = useState(0);
  const prevViewRef = useRef("studio");

  const handleNavigate = (next) => {
    if (!session && PROTECTED_VIEWS.has(next)) {
      setPendingView(next);
      const from = VIEW_ORDER[prevViewRef.current] ?? 0;
      const to = VIEW_ORDER.login;
      setNavDir(to === from ? 0 : to > from ? 1 : -1);
      prevViewRef.current = "login";
      setView("login");
      return;
    }

    if (next === "dashboard") {
      setDashboardKey((k) => k + 1);
    }

    const from = VIEW_ORDER[prevViewRef.current] ?? 0;
    const to = VIEW_ORDER[next] ?? 0;
    setNavDir(to === from ? 0 : to > from ? 1 : -1);
    prevViewRef.current = next;
    setView(next);
    if (!session || next === "login") {
      setPendingView(null);
    }
  };

  const handleAuthClick = async () => {
    if (session) {
      await signOut();
      setPendingView(null);
      prevViewRef.current = "login";
      setView("login");
      setNavDir(0);
      return;
    }
    handleNavigate("login");
  };

  useEffect(() => {
    if (!session || !pendingView) return;
    const target = pendingView;
    setPendingView(null);
    prevViewRef.current = target;
    setView(target);
    setNavDir(0);
  }, [session, pendingView]);

  // After sign-in from the login screen (no pending protected view), land on Dashboard.
  useEffect(() => {
    if (!session || pendingView) return;
    if (view !== "login") return;
    prevViewRef.current = "dashboard";
    setView("dashboard");
    setNavDir(0);
  }, [session, view, pendingView]);

  useEffect(() => {
    if (!passwordRecovery || !session) return;
    prevViewRef.current = "settings";
    setView("settings");
    setNavDir(0);
    setPendingView(null);
  }, [passwordRecovery, session]);

  // Keep the document locked to the viewport; panels scroll internally.
  useEffect(() => {
    document.body.classList.remove("app-scroll");
    document.documentElement.classList.add("app-viewport-lock");
    document.body.classList.add("app-viewport-lock");
    return () => {
      document.documentElement.classList.remove("app-viewport-lock");
      document.body.classList.remove("app-viewport-lock");
    };
  }, []);

  if (loading) {
    return (
      <div className="App app-shell app-shell--loading">
        <div className="auth-loading">Loading…</div>
      </div>
    );
  }

  const needsAuth = !session;
  const showProtected = Boolean(session) && PROTECTED_VIEWS.has(view);

  const stageDirClass =
    navDir > 0
      ? "view-stage--forward"
      : navDir < 0
        ? "view-stage--back"
        : "";

  const activeNavView = !session
    ? pendingView || (PROTECTED_VIEWS.has(view) ? view : "login")
    : view;

  return (
    <div className={`App App--${view} app-shell`}>
      <Navbar
        view={activeNavView}
        onNavigate={handleNavigate}
        onAuthClick={handleAuthClick}
        isLoggedIn={Boolean(session)}
        userEmail={user?.email}
      />
      <header className="App-header">
        <div className="container">
          <div className="big-title app-brand-title">
            Argon-Web
            <br />
            <span className="title-gradient">Studio.</span>
          </div>
          <div
            key={needsAuth ? "login" : view}
            className={`view-stage view-stage--${needsAuth ? "login" : view} ${stageDirClass}`.trim()}
          >
            {needsAuth && <AuthScreen embedded />}
            {showProtected && view === "studio" && (
              <Studio onBackToDashboard={() => handleNavigate("dashboard")} />
            )}
            {showProtected && view === "dashboard" && (
              <Dashboard key={dashboardKey} />
            )}
            {showProtected && view === "settings" && <Profile />}
          </div>
        </div>
      </header>
    </div>
  );
}

export default App;
